<template>
  <v-dialog :model-value="modelValue" max-width="900px" persistent scrollable>
    <v-card>
      <v-card-title>上位計画から転記</v-card-title>
      <v-card-text>
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        <template v-else>
          <div v-if="candidates.length === 0" class="text-body-2 text-grey py-4">
            転記できる項目がありません。帳票設定の転記ルールを確認してください。
          </div>
          <template v-else>
            <div class="d-flex align-center mb-2">
              <v-checkbox
                :model-value="isAllSelected"
                :indeterminate="isPartiallySelected"
                label="選べる項目をすべて選択"
                density="compact"
                hide-details
                color="primary"
                @update:model-value="toggleAll"
              ></v-checkbox>
              <v-spacer />
              <v-checkbox
                v-model="overwrite"
                label="入力済みの項目も上書きする"
                density="compact"
                hide-details
                color="warning"
              ></v-checkbox>
            </div>

            <v-alert v-if="overwrite && overwriteCount > 0" type="warning" variant="tonal" density="compact" class="mb-3">
              入力済みの{{ overwriteCount }}項目が上位計画の内容で置き換わります。
            </v-alert>

            <v-table density="compact">
              <thead>
                <tr>
                  <th style="width: 48px"></th>
                  <th style="width: 20%">項目</th>
                  <th style="width: 32%">現在の内容</th>
                  <th style="width: 32%">転記される内容</th>
                  <th style="width: 16%">参照元</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="candidate in candidates" :key="candidate.itemKey">
                  <td>
                    <v-checkbox
                      :model-value="selectedItemKeys.includes(candidate.itemKey)"
                      :disabled="!isSelectable(candidate)"
                      density="compact"
                      hide-details
                      color="primary"
                      @update:model-value="toggle(candidate.itemKey)"
                    ></v-checkbox>
                  </td>
                  <td>
                    <div>{{ candidate.itemName }}</div>
                    <div
                      v-if="candidate.resolution !== 'resolved'"
                      class="text-caption text-warning"
                    >
                      {{ resolutionLabel(candidate) }}
                    </div>
                    <!--
                      入力済みの項目は上書きしない限り転記されない。
                      選べるのに結果だけスキップされると理由が分からないので、
                      選択不可にしたうえで必要な操作を書く。
                    -->
                    <div
                      v-else-if="needsOverwrite(candidate)"
                      class="text-caption text-grey-darken-1"
                    >
                      入力済みのため「上書きする」が必要です
                    </div>
                  </td>
                  <td class="tp-transfer__value">
                    <span v-if="candidate.currentValue">{{ candidate.currentValue }}</span>
                    <span v-else class="text-grey">（未入力）</span>
                  </td>
                  <td class="tp-transfer__value">
                    <span v-if="candidate.candidateValue">{{ candidate.candidateValue }}</span>
                    <span v-else class="text-grey">（転記元が未入力）</span>
                  </td>
                  <td class="text-caption">{{ candidate.sourcePlanLabel || "—" }}</td>
                </tr>
              </tbody>
            </v-table>
          </template>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" :disabled="transferring" @click="$emit('update:modelValue', false)">
          キャンセル
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="transferring"
          :disabled="selectedItemKeys.length === 0"
          @click="execute"
        >
          転記する（{{ selectedItemKeys.length }}件）
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { NurseryTeachingPlanTransferCandidate } from "chaild-api/lib";
import ApiTeachingPlan from "@/api/ApiTeachingPlan";
import { resolutionLabelOf } from "@/constants/teachingPlan";
import { notifySuccess } from "@/utils/TeachingPlanDialog";

/**
 * 一括転記モーダル (api-design.md §5.3)。
 * 転記元が解決できない項目は選択できない。上書きは明示的にチェックしたときのみ行う。
 */
export default defineComponent({
  name: "TeachingPlanTransferDialog",

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    planId: {
      type: String,
      required: true,
    },
  },

  emits: ["update:modelValue", "transferred"],

  data() {
    return {
      loading: false,
      loadToken: 0,
      transferring: false,
      overwrite: false,
      candidates: [] as NurseryTeachingPlanTransferCandidate[],
      selectedItemKeys: [] as string[],

    };
  },

  computed: {
    /** いま実際に選べる候補 (上書きの有無で変わる) */
    selectableCandidates(): NurseryTeachingPlanTransferCandidate[] {
      return this.candidates.filter((candidate) => this.isSelectable(candidate));
    },
    isAllSelected(): boolean {
      return (
        this.selectableCandidates.length > 0 &&
        this.selectableCandidates.every((candidate) =>
          this.selectedItemKeys.includes(candidate.itemKey)
        )
      );
    },
    isPartiallySelected(): boolean {
      return this.selectedItemKeys.length > 0 && !this.isAllSelected;
    },
    /** 選択中のうち、既に値が入っている項目の数 */
    overwriteCount(): number {
      return this.candidates.filter(
        (candidate) =>
          this.selectedItemKeys.includes(candidate.itemKey) &&
          candidate.currentValue !== null &&
          candidate.currentValue !== ""
      ).length;
    },
  },

  watch: {
    modelValue(opened: boolean) {
      if (opened) {
        this.load();
      }
    },
    /** 上書きをやめたら、入力済みの項目は選べなくなるので選択から外す */
    overwrite() {
      this.selectedItemKeys = this.selectedItemKeys.filter((itemKey) => {
        const candidate = this.candidates.find((item) => item.itemKey === itemKey);
        return candidate != null && this.isSelectable(candidate);
      });
    },
  },

  methods: {
    /** 転記元帳票が特定できたかで sourceMissing の理由を出し分ける */
    resolutionLabel(candidate: NurseryTeachingPlanTransferCandidate): string {
      return resolutionLabelOf(candidate.resolution, candidate.sourcePlanId !== null);
    },
    /** 転記元から値を引けるか (上書きの要否とは別) */
    isTransferable(candidate: NurseryTeachingPlanTransferCandidate): boolean {
      return (
        candidate.resolution === "resolved" &&
        candidate.candidateValue !== null &&
        candidate.candidateValue !== ""
      );
    },
    /** 入力済みで、上書きしない限りスキップされる項目か */
    needsOverwrite(candidate: NurseryTeachingPlanTransferCandidate): boolean {
      return (
        this.isTransferable(candidate) &&
        candidate.currentValue !== null &&
        candidate.currentValue !== ""
      );
    },
    /** 実際に転記される見込みがあるものだけ選ばせる (選べてスキップは理由が伝わらない) */
    isSelectable(candidate: NurseryTeachingPlanTransferCandidate): boolean {
      if (!this.isTransferable(candidate)) {
        return false;
      }
      return this.overwrite || !this.needsOverwrite(candidate);
    },
    toggle(itemKey: string) {
      // 上書きの切り替えや再読込と同時に押されても、選べないものを選択に残さない
      const candidate = this.candidates.find((item) => item.itemKey === itemKey);
      if (!candidate || !this.isSelectable(candidate)) {
        this.selectedItemKeys = this.selectedItemKeys.filter((key) => key !== itemKey);
        return;
      }
      if (this.selectedItemKeys.includes(itemKey)) {
        this.selectedItemKeys = this.selectedItemKeys.filter((key) => key !== itemKey);
      } else {
        this.selectedItemKeys.push(itemKey);
      }
    },
    toggleAll() {
      if (this.isAllSelected) {
        this.selectedItemKeys = [];
        return;
      }
      this.selectedItemKeys = this.selectableCandidates.map((candidate) => candidate.itemKey);
    },
    async load() {
      const planId = this.planId;
      this.loadToken += 1;
      const token = this.loadToken;
      this.loading = true;
      this.overwrite = false;
      this.selectedItemKeys = [];
      const response = await ApiTeachingPlan.listTransferCandidates({
        planId,
      }).catch(() => null);
      // 開き直し・別帳票への切り替えの後に古い応答が返ると、前の帳票の候補で
      // 上書きされ、そのまま実行すると現在の帳票へ別帳票のitemKeyを送ってしまう
      if (token !== this.loadToken || this.planId !== planId) {
        return;
      }
      this.loading = false;
      this.candidates = response ? response.data : [];
      // 既定は未入力の項目だけを選択する (誤って上書きしないため)。
      // 上書きは既定でOFFなので、選べる候補と一致する
      this.selectedItemKeys = this.selectableCandidates.map((candidate) => candidate.itemKey);
    },
    async execute() {
      if (this.selectedItemKeys.length === 0) {
        return;
      }
      this.transferring = true;
      const response = await ApiTeachingPlan.transfer({
        planId: this.planId,
        itemKeys: this.selectedItemKeys,
        overwrite: this.overwrite,
      }).catch(() => null);
      this.transferring = false;
      if (!response) {
        return;
      }
      // 入力済みは上書きを選ばない限り選択できないので通常は0件だが、
      // 開いている間に他の端末で入力された場合に備えて残す
      const skippedByValue = response.skipped.filter((item) => item.reason === "hasValue").length;
      if (skippedByValue > 0) {
        notifySuccess(
          `${response.transferred.length}項目を転記しました`,
          `入力済みの${skippedByValue}項目はスキップしました。`
        );
      }
      this.$emit("transferred");
      this.$emit("update:modelValue", false);
    },
  },
});
</script>

<style scoped>
.tp-transfer__value {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  max-width: 0;
}
</style>
